import { DOMExportOutput, DecoratorNode, EditorConfig, LexicalEditor, LexicalNode, NodeKey } from 'lexical'
import { ReactNode } from 'react'
import MergeTag from './MergeTag'
import { getTextThemeClassNames } from './utils'
import { LEXICAL_TEXT_CLASSES } from '../theme'
import { MergeFieldSetting } from '@web/graphql-types'

type Styles = { [key: string]: string | number | undefined | null }
type MergeTagValue = Partial<MergeFieldSetting>

interface SerializedMergeTagNode {
  type: string
  version: number
  value: MergeTagValue
  fallback: string | undefined
  options: string | undefined
  format: number
  styles: Styles
}

export class MergeTagNode extends DecoratorNode<ReactNode> {
  __value: MergeTagValue
  __format: number
  __styles: Styles
  __fallback: string | undefined
  __options: string | undefined

  static getType(): string {
    return 'merge-tag'
  }

  static clone(node: MergeTagNode): MergeTagNode {
    return new MergeTagNode(node.__value, node.__format, node.__styles, node.__fallback, node.__options, node.__key)
  }

  constructor(
    value: MergeTagValue,
    format?: number,
    styles?: Styles,
    fallback?: string,
    options?: string,
    key?: NodeKey,
  ) {
    super(key)
    this.__value = value
    this.__format = format ?? 0
    this.__styles = styles ?? {}
    this.__fallback = fallback
    this.__options = options
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement('span')

    return dom
  }

  updateDOM(_prevNode: unknown, _dom: HTMLElement, _config: EditorConfig): boolean {
    return false
  }

  getFormat(): number {
    return this.__format
  }

  getStyles(): Styles {
    return this.__styles
  }

  getValue(): MergeTagValue {
    return this.__value
  }

  getFallback(): string | undefined {
    return this.__fallback
  }

  getOptions(): string | undefined {
    return this.__options
  }

  setValue(value: MergeTagValue): void {
    const writable = this.getWritable()
    writable.__value = value
  }

  setFormat(format: number): void {
    const writable = this.getWritable()
    writable.__format = writable.__format ^ format
  }

  setStyles(styles: Styles): void {
    const writable = this.getWritable()
    writable.__styles = { ...writable.__styles, ...styles }
  }

  setFallback(fallback: string): void {
    const writable = this.getWritable()
    writable.__fallback = fallback
  }

  setOptions(options: string): void {
    const writable = this.getWritable()
    writable.__options = options
  }

  decorate(editor: LexicalEditor): ReactNode {
    const {
      'background-color': backgroundColor,
      'font-size': fontSize,
      'font-family': fontFamily,
      ...styles
    } = this.getStyles()
    return (
      <span
        className={getTextThemeClassNames(0, this.getFormat(), LEXICAL_TEXT_CLASSES).join(' ')}
        style={{
          ...styles,
          backgroundColor: `${backgroundColor || ''}`,
          fontSize: `${fontSize || ''}`,
          fontFamily: `${fontFamily || ''}`,
        }}
      >
        <MergeTag
          nodeKey={this.__key}
          editor={editor}
          value={this.getValue()}
          fallback={this.getFallback()}
          options={this.getOptions()}
          onFormat={(format) => this.setFormat(format)}
          onUpdate={(value) => {
            if (value.MergeFieldSettingId || value.MergeTag) {
              this.setValue({ ...this.getValue(), ...value })
            }

            if (value.MergeTagFallback !== undefined) {
              this.setFallback(value.MergeTagFallback ?? '')
            }

            if (value.MergeTagOptions) {
              this.setOptions(value.MergeTagOptions)
            }
          }}
        />
      </span>
    )
  }

  exportJSON(): SerializedMergeTagNode {
    return {
      type: this.getType(),
      version: 1,
      value: this.getValue(),
      format: this.getFormat(),
      styles: this.getStyles(),
      fallback: this.getFallback(),
      options: this.getOptions(),
    }
  }

  exportDOM(): DOMExportOutput {
    const mergeTag = document.createElement('span')
    const value = this.getValue()
    mergeTag.dataset['mergeTag'] = 'true'
    mergeTag.dataset['mergeTagValue'] = value.MergeFieldSettingId || ''
    mergeTag.dataset['mergeTagFallback'] = this.getFallback() || ''
    mergeTag.dataset['mergeTagType'] = value.MergeTagType || ''
    mergeTag.dataset['mergeTagOptions'] = this.getOptions() || ''
    mergeTag.innerText = value.MergeTag || ''
    mergeTag.className = getTextThemeClassNames(0, this.getFormat(), LEXICAL_TEXT_CLASSES).join(' ')
    mergeTag.style.cssText = Object.entries(this.getStyles())
      .map(([key, value]) => (value ? `${key}:${value}` : ''))
      .join(';')

    return {
      element: mergeTag,
    }
  }

  static importJSON(serializedNode: SerializedMergeTagNode): MergeTagNode {
    if (typeof serializedNode.value === 'string') {
      const value = serializedNode.value === 'first_name' ? 'First Name' : serializedNode.value
      serializedNode.value = {
        MergeFieldSettingId: value,
        MergeTag: value,
        QueryParameter: '',
        CsvHeader: serializedNode.value,
      }
    }
    const node = $createMergeTagNode(
      serializedNode.value,
      serializedNode.format,
      serializedNode.styles,
      serializedNode.fallback,
      serializedNode.options,
    )

    return node
  }
}

export function $createMergeTagNode(
  value: MergeTagValue,
  format?: number,
  styles?: Styles,
  fallback?: string,
  options?: string,
): MergeTagNode {
  return new MergeTagNode(
    value,
    format,
    styles,
    fallback ?? value.MergeTagFallback ?? undefined,
    options ?? value.MergeTagOptions ?? undefined,
  )
}

export function $isMergeTagNode(node: LexicalNode): boolean {
  return node instanceof MergeTagNode
}
